import React, { useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { FaSearch, FaDownload } from 'react-icons/fa';
import { Drawer, AgGridTable, Pagination, Button } from 'components/common';
import { debounce } from 'helpers/debounce';

/**
 * Reusable drill-down detail drawer for the booking dashboards.
 * Server-side paginated + quick search + server-side Excel export.
 */
const DrilldownDrawer = ({ show, onClose, item, baseParams, fetchThunk, exportThunk, columnDefs, unit = 'record' }) => {
  const dispatch = useDispatch();
  const [state, setState] = useState({ rows: [], total: 0, page: 1, pageSize: 20, loading: false });
  const [search, setSearch] = useState('');
  const searchRef = useRef('');

  const dim = item?.dimension;
  const val = item?.key;

  const fetchPage = useCallback((page = 1, pageSize = 20) => {
    if (!item) return;
    setState((s) => ({ ...s, loading: true }));
    dispatch(fetchThunk({
      ...baseParams, dimension: dim, value: val, page, per_page: pageSize,
      ...(searchRef.current ? { search: searchRef.current } : {}),
    })).then((res) => {
      const p = res.payload || {};
      setState({ rows: p.rows || [], total: p.total_rows || 0, page, pageSize, loading: false });
    });
  }, [dispatch, item, baseParams, fetchThunk, dim, val]);

  // Reset + load page 1 when a new chart segment is opened.
  useEffect(() => {
    if (show && item) { setSearch(''); searchRef.current = ''; fetchPage(1, 20); }
    // reload only when a new segment is opened; fetchPage changes with the parent's baseParams object and would wipe the search
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, dim, val]);

  // Keep the debounced search pointing at the latest fetchPage (avoids a stale
  // closure that captured fetchPage while item was still null).
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;
  const debounced = useRef(debounce(() => fetchRef.current(1, 20), 500)).current;
  const onSearch = (e) => { const v = e.target.value; setSearch(v); searchRef.current = v; debounced(); };
  const onExport = () => { if (item) dispatch(exportThunk({ ...baseParams, dimension: dim, value: val, ...(searchRef.current ? { search: searchRef.current } : {}) })); };

  return (
    <Drawer show={show} onHide={onClose} title={item ? `${item.label} — ${state.total} ${unit}(s)` : ''} width="60%">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-mb-2">
        <div className="tw-relative tw-flex tw-items-center tw-w-64">
          <FaSearch className="tw-absolute tw-left-3 tw-text-slate-400 tw-pointer-events-none" size={12} />
          <input
            type="text"
            value={search}
            onChange={onSearch}
            placeholder="Search..."
            className="tw-w-full tw-pl-8 tw-pr-3 tw-py-1.5 tw-text-sm tw-border tw-border-solid tw-border-slate-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 tw-bg-white tw-text-slate-700"
          />
        </div>
        <Button variant="secondary" size="sm" leftIcon={FaDownload} disabled={!state.total} onClick={onExport}>Export Excel</Button>
      </div>
      <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-overflow-hidden">
        <AgGridTable rowData={state.rows} columnDefs={columnDefs} pagination={false} filter={false} height="65vh" loading={state.loading} />
        <Pagination
          currentPage={state.page}
          totalPages={Math.max(1, Math.ceil(state.total / state.pageSize))}
          totalResults={state.total}
          perPage={state.pageSize}
          onPageChange={(p) => fetchPage(p, state.pageSize)}
          onPageSizeChange={(sz) => fetchPage(1, sz)}
          pageSizeOptions={[20, 50, 100]}
        />
      </div>
    </Drawer>
  );
};

DrilldownDrawer.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  item: PropTypes.object,
  baseParams: PropTypes.object,
  fetchThunk: PropTypes.func.isRequired,
  exportThunk: PropTypes.func.isRequired,
  columnDefs: PropTypes.array.isRequired,
  unit: PropTypes.string,
};

export default DrilldownDrawer;
